import {
  APPROVAL_DOC_TYPES,
  type ApprovalDelegation,
  type ApprovalDocType,
  type TenantUser,
} from "@station/contracts";
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  DateInput,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, UserCheck } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApprovalDelegations, useRefreshApprovals } from "../../../features/approvals/queries.js";
import {
  ApprovalsPage,
  docTypeLabel,
  useApprovalPermissions,
  useApprovalsCrumb,
} from "../../../features/approvals/shared.js";
import { todayIstanbul } from "../../../features/accounting/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";

export const Route = createFileRoute("/_app/onaylar/vekaletler")({ component: DelegationsPage });

/** Tarih girdisi (gün) → gün başı / gün sonu zaman damgası (Europe/Istanbul). */
function toTimestamp(date: string, endOfDay: boolean): string {
  return new Date(`${date}T${endOfDay ? "23:59:59" : "00:00:00"}+03:00`).toISOString();
}

function DelegationsPage(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const crumb = useApprovalsCrumb();
  const permissions = useApprovalPermissions();
  const refresh = useRefreshApprovals();
  const [scope, setScope] = useState<"MINE" | "ALL">("MINE");
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApprovalDelegation | null>(null);

  const delegations = useApprovalDelegations({
    scope: permissions.canManageDelegation ? scope : "MINE",
    includeInactive: true,
  });

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      api.POST("/approval-delegations/{id}/revoke", { params: { path: { id } } }),
    onSuccess: async () => {
      setRevoking(null);
      await refresh();
      toast({ tone: "success", title: t("approvals.delegations.revoked") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const columns = useMemo<DataTableColumn<ApprovalDelegation>[]>(
    () => [
      {
        id: "delegator",
        header: t("approvals.delegations.delegator"),
        hideable: false,
        cell: (row) => row.delegatorName,
      },
      {
        id: "delegate",
        header: t("approvals.delegations.delegate"),
        hideable: false,
        cell: (row) => row.delegateName,
      },
      {
        id: "period",
        header: t("approvals.delegations.period"),
        cell: (row) =>
          `${formatDate(row.validFrom)} – ${row.validTo ? formatDate(row.validTo) : t("approvals.delegations.openEnded")}`,
      },
      {
        id: "docTypes",
        header: t("approvals.fields.docType"),
        cell: (row) =>
          row.docTypes.length === 0
            ? t("approvals.delegations.allDocTypes")
            : row.docTypes.map(docTypeLabel).join(", "),
      },
      { id: "reason", header: t("approvals.fields.note"), cell: (row) => row.reason ?? "—" },
      {
        id: "status",
        header: t("common.status"),
        hideable: false,
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("approvals.delegations.active") : t("approvals.delegations.revokedLabel")}
          </StatusBadge>
        ),
      },
      {
        id: "actions",
        header: t("common.actions"),
        align: "right",
        cell: (row) =>
          row.isActive ? (
            <Button variant="ghost" size="sm" onClick={() => setRevoking(row)}>
              {t("approvals.delegations.revoke")}
            </Button>
          ) : null,
      },
    ],
    [t],
  );

  return (
    <ApprovalsPage
      title={t("approvals.delegations.title")}
      description={t("approvals.delegations.description")}
      breadcrumb={[crumb, { label: t("approvals.delegations.title") }]}
      actions={
        <Button onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" />
          {t("approvals.delegations.new")}
        </Button>
      }
    >
      <DataTable
        caption={t("approvals.delegations.title")}
        columns={columns}
        rows={delegations.data ?? []}
        rowKey={(row) => row.id}
        loading={delegations.isPending}
        error={delegations.error ? <Alert tone="danger">{errorMessage(delegations.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<UserCheck />}
            title={t("approvals.delegations.emptyTitle")}
            description={t("approvals.delegations.emptyDescription")}
          />
        }
        filters={
          permissions.canManageDelegation ? (
            <FormField label={t("approvals.delegations.scope")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={scope}
                  onChange={(event) => setScope(event.target.value as "MINE" | "ALL")}
                >
                  <option value="MINE">{t("approvals.delegations.scopeMine")}</option>
                  <option value="ALL">{t("approvals.delegations.scopeAll")}</option>
                </Select>
              )}
            </FormField>
          ) : undefined
        }
      />

      <Dialog
        open={creating}
        onOpenChange={setCreating}
        title={t("approvals.delegations.new")}
        description={t("approvals.delegations.newDescription")}
      >
        <DelegationForm
          canDelegateForOthers={permissions.canManageDelegation}
          onDone={() => setCreating(false)}
          onCancel={() => setCreating(false)}
        />
      </Dialog>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null);
        }}
        tone="danger"
        title={t("approvals.delegations.revokeTitle")}
        description={t("approvals.delegations.revokeDescription")}
        confirmLabel={t("approvals.delegations.revoke")}
        onConfirm={() => {
          if (revoking) revoke.mutate(revoking.id);
        }}
      />
    </ApprovalsPage>
  );
}

function DelegationForm({
  canDelegateForOthers,
  onDone,
  onCancel,
}: {
  canDelegateForOthers: boolean;
  onDone: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshApprovals();
  const [delegatorUserId, setDelegatorUserId] = useState("");
  const [delegateUserId, setDelegateUserId] = useState("");
  const [validFrom, setValidFrom] = useState(todayIstanbul() as string);
  const [validTo, setValidTo] = useState("");
  const [docTypes, setDocTypes] = useState<ApprovalDocType[]>([]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: async () => (await api.GET("/users")).data!.items as TenantUser[],
  });

  const create = useMutation({
    mutationFn: async () =>
      api.POST("/approval-delegations", {
        body: {
          delegatorUserId: delegatorUserId || null,
          delegateUserId,
          validFrom: toTimestamp(validFrom, false),
          validTo: validTo ? toTimestamp(validTo, true) : null,
          docTypes,
          reason: reason.trim() || null,
        },
      }),
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("approvals.delegations.created") });
      onDone();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <form
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {canDelegateForOthers ? (
        <FormField
          label={t("approvals.delegations.delegator")}
          description={t("approvals.delegations.delegatorHint")}
        >
          {(control) => (
            <Select
              {...control}
              value={delegatorUserId}
              onChange={(event) => setDelegatorUserId(event.target.value)}
            >
              <option value="">{t("approvals.delegations.delegatorSelf")}</option>
              {(users.data ?? []).map((user) => (
                <option key={user.userId} value={user.userId}>
                  {user.fullName}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      ) : null}
      <FormField label={t("approvals.delegations.delegate")} required>
        {(control) => (
          <Select
            {...control}
            required
            value={delegateUserId}
            onChange={(event) => setDelegateUserId(event.target.value)}
          >
            <option value="">{t("common.select")}</option>
            {(users.data ?? []).map((user) => (
              <option key={user.userId} value={user.userId}>
                {user.fullName}
              </option>
            ))}
          </Select>
        )}
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t("approvals.delegations.validFrom")} required>
          {(control) => (
            <DateInput {...control} value={validFrom} onValueChange={(value) => setValidFrom(value ?? "")} />
          )}
        </FormField>
        <FormField
          label={t("approvals.delegations.validTo")}
          description={t("approvals.delegations.validToHint")}
        >
          {(control) => (
            <DateInput {...control} value={validTo} onValueChange={(value) => setValidTo(value ?? "")} />
          )}
        </FormField>
      </div>
      <fieldset className="grid gap-2 rounded-md border p-3">
        <legend className="px-1 text-sm font-medium">{t("approvals.fields.docType")}</legend>
        <p className="text-xs text-muted-foreground">{t("approvals.delegations.docTypesHint")}</p>
        <div className="grid gap-1 sm:grid-cols-2">
          {APPROVAL_DOC_TYPES.map((type) => (
            <label key={type} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={docTypes.includes(type)}
                onChange={(event) =>
                  setDocTypes(
                    event.target.checked ? [...docTypes, type] : docTypes.filter((item) => item !== type),
                  )
                }
              />
              {docTypeLabel(type)}
            </label>
          ))}
        </div>
      </fieldset>
      <FormField label={t("approvals.delegations.reason")}>
        {(control) => (
          <Textarea
            {...control}
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </FormField>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={create.isPending} disabled={delegateUserId === ""}>
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
